/**
 * Static sidebar nav structure — 4 labeled sections (Overview / Records /
 * Insights / Setup), matching the reference `Sidebar.tsx`'s `NAV: NavGroup[]`
 * shape.
 *
 * This module is intentionally data-only (no hooks, no JSX rendering) so it
 * can be imported by both `AppShell` and tests without pulling in React
 * Query / auth context. Anything that varies per-render (the Leads unread
 * badge count, role visibility) is resolved by the consumer (`NavSection`)
 * at render time — looked up by each item's stable `id`, never by matching
 * `label` strings, so a copy change can never silently break badge wiring
 * or section-emptiness detection.
 */
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
// Marketing has no app.zip equivalent to port an icon from — kept as a
// plain MUI icon. Every other item below uses the pixel-exact app.zip
// icons in `navIcons.tsx`, not this import.
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
// SPIFF registrations — a distinct stock MUI icon so the three Marketing
// children don't all render the same tag (same rationale as LocalOfferIcon:
// no app.zip equivalent to port).
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
// Client Success has no app.zip equivalent to port an icon from — same
// rationale as LocalOfferIcon/AppRegistrationIcon above.
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import RedeemIcon from '@mui/icons-material/Redeem';
import { Roles, type Role } from '@/shared/auth/roles';
import type { DeploymentEnv } from '@/env';
import {
  NavIconDashboard,
  NavIconSales,
  NavIconClients,
  NavIconContacts,
  NavIconLeads,
  NavIconPartners,
  NavIconOpportunities,
  NavIconReports,
  NavIconForecast,
  NavIconChat,
  NavIconAdmin,
} from './navIcons';

/**
 * Widened past MUI's own `SvgIconComponent` (from `@mui/icons-material`) so
 * this field accepts BOTH a stock MUI icon (e.g. `LocalOfferIcon`, for
 * Marketing) and the app.zip-ported custom icons in `navIcons.tsx` — plain
 * function components that accept `SvgIconProps` but aren't themselves
 * `OverridableComponent`s, so they don't satisfy `SvgIconComponent`'s
 * stricter shape.
 */
export type NavIconComponent = ComponentType<SvgIconProps>;

export interface NavItemConfig {
  /**
   * Explicit discriminant (vs. `NavGroupConfig`'s `type: 'group'`) so
   * `NavEntryConfig` narrows correctly on `entry.type === 'group'` — without
   * this, TypeScript can't discriminate a union where only one member
   * declares the tag field at all.
   */
  readonly type?: 'item';
  /** Stable identity used for `key`s and badge/role lookups — never the label. */
  readonly id: string;
  readonly to: string;
  readonly label: string;
  readonly icon: NavIconComponent;
  readonly requiresRole?: Role;
  /**
   * Visibility hint ONLY — never a security control. The real gate for a
   * dev-only feature is the backend not mounting the route at all (a true
   * 404 outside dev, per `qfly_shared.api.app_factory.resolve_docs_urls`'s
   * precedent); this just avoids showing a dead link. Currently only `'dev'`
   * is a meaningful value (Dashboard V2, Phase 1+2).
   */
  readonly requiresEnv?: Extract<DeploymentEnv, 'dev'>;
  /**
   * Passed straight through to `NavLink`'s `end` prop. Only needed when this
   * item's `to` is a URL prefix of a SIBLING item's `to` (e.g. `/dashboard`
   * is a prefix of `/dashboard/fytd`) — without it, React Router's default
   * prefix matching leaves both items highlighted active at once on the
   * longer route. Do not set this on items whose own sub-routes (a detail
   * page, not a separate nav destination) should keep the parent item
   * highlighted — e.g. Clients should stay active on `/clients/:id`.
   */
  readonly end?: boolean;
}

/**
 * A collapsible sub-group nested inside a section's flat item list — e.g.
 * "Marketing" (Campaigns + Leads) inside Records. Distinct from
 * `NavSectionConfig`: a section is a static, always-open label; a group is
 * a toggleable header with its own expand/collapse state (persisted per
 * `id`) rendered via `NavGroup`.
 */
export interface NavGroupConfig {
  readonly type: 'group';
  /** Stable identity — used for the `key`, the localStorage persistence key, and badge lookups on the group header. */
  readonly id: string;
  readonly label: string;
  readonly icon: NavIconComponent;
  readonly children: readonly NavItemConfig[];
}

export type NavEntryConfig = NavItemConfig | NavGroupConfig;

export interface NavSectionConfig {
  readonly id: string;
  readonly label: string;
  readonly items: readonly NavEntryConfig[];
}

export const NAV_SECTIONS: readonly NavSectionConfig[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      {
        id: 'dashboard',
        to: '/dashboard',
        label: 'Dashboard',
        icon: NavIconDashboard,
        // '/dashboard' is a URL prefix of the Sales (FYTD) item below
        // ('/dashboard/fytd') — without `end`, both light up together
        // whenever Sales (FYTD) is the active page.
        end: true,
      },
      { id: 'sales-fytd', to: '/dashboard/fytd', label: 'Sales (FYTD)', icon: NavIconSales },
      // Dashboard V2 — live everywhere as of 2026-10-08 (explicit, repeated
      // user request; the former "dev shows both V1 and V2; prod shows V1
      // only" architecture decision was reversed in the same release as the
      // backend's dev-only router mount — see app/main.py and
      // dashboardV2RouteGate.tsx). Shown ALONGSIDE Dashboard/Sales (FYTD)
      // above, never replacing them. Reuses NavIconDashboard deliberately
      // (not a new icon) — this is the next iteration of the same
      // destination, not a conceptually different one.
      {
        id: 'dashboard-v2',
        to: '/dashboard/v2',
        label: 'Dashboard (V2)',
        icon: NavIconDashboard,
      },
    ],
  },
  {
    id: 'records',
    label: 'Records',
    items: [
      { id: 'clients', to: '/clients', label: 'Clients', icon: NavIconClients },
      { id: 'contacts', to: '/contacts', label: 'Contacts', icon: NavIconContacts },
      // Marketing stays a collapsible sub-group (not flattened) — nested
      // under Records rather than living as its own top-level section, per
      // product direction (2026-08). Campaigns/Leads keep their own routes,
      // icons, and (for Leads) live badge unchanged; only their placement
      // in the nav tree moved.
      {
        type: 'group',
        id: 'marketing',
        label: 'Marketing',
        // No app.zip equivalent exists for this group — Marketing isn't a
        // nav entry in the reference at all — so this stays a plain MUI icon
        // rather than inventing a fake "Figma" one.
        icon: LocalOfferIcon,
        children: [
          { id: 'campaigns', to: '/campaigns', label: 'Campaigns', icon: LocalOfferIcon },
          { id: 'leads', to: '/leads', label: 'Leads', icon: NavIconLeads },
          {
            id: 'registrations',
            to: '/spiff',
            label: 'Registrations',
            icon: AppRegistrationIcon,
          },
          {
            id: 'spiff-payouts',
            to: '/spiff-payouts',
            label: 'SPIFF Payouts',
            icon: RedeemIcon,
            requiresRole: Roles.Admin,
          },
        ],
      },
      { id: 'partners', to: '/partners', label: 'Partners', icon: NavIconPartners },
      {
        id: 'opportunities',
        to: '/opportunities',
        label: 'Opportunities',
        icon: NavIconOpportunities,
      },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    items: [
      { id: 'reports', to: '/reports', label: 'Reports', icon: NavIconReports },
      { id: 'forecast', to: '/forecast', label: 'Forecast', icon: NavIconForecast },
      // Phase 0 scaffolding — see ClientSuccessPage.tsx. No requiresRole:
      // per decision D13 (existing 3 roles only), record-level ownership is
      // enforced at the API layer once Phase 1 endpoints exist, not by
      // hiding the nav entry from a role.
      {
        id: 'client-success',
        to: '/client-success',
        label: 'Client Success',
        icon: HealthAndSafetyOutlinedIcon,
      },
      { id: 'eq-chat', to: '/eq', label: 'EQ Chat', icon: NavIconChat },
    ],
  },
  {
    id: 'setup',
    label: 'Setup',
    items: [
      {
        id: 'admin',
        to: '/admin',
        label: 'Admin',
        icon: NavIconAdmin,
        requiresRole: Roles.Admin,
      },
    ],
  },
] as const;

/** Item ids that carry a live badge — currently only Leads' unread count. */
export const LEADS_NAV_ITEM_ID = 'leads';
